import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DateRangePicker from "@/components/DateRangePicker";
import WeeklyBarChart from "@/components/WeeklyBarChart";
import WeeklyHeatmap from "@/components/WeeklyHeatmap";
import MonthlyUsageChart from "@/components/MonthlyUsageChart";
import MonthlyYoyTable from "@/components/MonthlyYoyTable";
import ProfileEditor from "@/components/ProfileEditor";
import ContactsEditor from "@/components/ContactsEditor";
import SalesRepsEditor from "@/components/SalesRepsEditor";
import {
  useCustomerDeviceUsage,
  useCustomerLocations,
  useCustomerMonthlyYoy,
  useCustomerWeekly,
  type CustomerLocation,
} from "@/lib/hooks";
import { presets, type Range } from "@/lib/dates";
import { fmtInt, fmtUSDCompact, fmtDate } from "@/lib/format";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";

const defaultPreset = presets[0];

function formatPrimaryLocation(loc: CustomerLocation | null | undefined): string | null {
  if (!loc) return null;
  const region = [loc.state, loc.postal_code].filter(Boolean).join(" ");
  const tail = loc.country && loc.country !== "US" ? loc.country : null;
  const place = [region, tail].filter(Boolean).join(" · ");
  const parts = [place, loc.primary_ship_to].filter(Boolean);
  return parts.length ? parts.join(" — ") : null;
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [range, setRange] = useState<Range>(defaultPreset.range());
  const [presetId, setPresetId] = useState<string | null>(defaultPreset.id);
  const [productGroupKey, setProductGroupKey] = useState<"catheters" | "all">("catheters");

  const productGroups = productGroupKey === "catheters" ? ["UOT", "IAP", "SmartFoley Adapter"] : [];
  const { data, isLoading, error } = useCustomerWeekly(id, range.from, range.to, productGroups);
  const deviceUsage = useCustomerDeviceUsage(id, range.from, range.to);
  const yoy = useCustomerMonthlyYoy(id); // all products, full history
  const locations = useCustomerLocations(id);

  const primaryLocation = formatPrimaryLocation(data?.location);

  const monthly = useMemo(() => {
    if (!data) return [];
    const acc: Record<string, { month: string; qty: number; revenue: number }> = {};
    for (const w of data.weeks) {
      const key = w.week.slice(0, 7); // YYYY-MM (first day of week's month)
      acc[key] ??= { month: key, qty: 0, revenue: 0 };
      acc[key].qty += w.qty;
      acc[key].revenue += w.revenue;
    }
    return Object.values(acc).sort((a, b) => a.month.localeCompare(b.month));
  }, [data]);

  const total = useMemo(() => {
    if (!data) return { qty: 0, revenue: 0, lastOrder: null as string | null };
    let qty = 0;
    let revenue = 0;
    let lastOrder: string | null = null;
    for (const w of data.weeks) {
      qty += w.qty;
      revenue += w.revenue;
      if (w.qty > 0 && (lastOrder == null || w.week > lastOrder)) lastOrder = w.week;
    }
    return { qty, revenue, lastOrder };
  }, [data]);

  const exportName = data?.customer_name ?? id ?? "customer";

  function exportMonthly() {
    downloadXlsx(
      stampedName(`${exportName}-monthly`),
      ["Month", "Quantity", "Revenue"],
      monthly.map((m) => [m.month, m.qty, m.revenue]),
    );
  }
  function exportYoy() {
    downloadXlsx(
      stampedName(`${exportName}-year-over-year`),
      ["Year", "Month", "Revenue", "Quantity"],
      (yoy.data?.cells ?? []).map((c) => [c.year, c.month, c.revenue, c.qty]),
    );
  }
  function exportLocations() {
    downloadXlsx(
      stampedName(`${exportName}-locations`),
      ["Ship-to site", "State", "Postal", "Country"],
      (locations.data?.locations ?? []).map((s) => [s.ship_to_name, s.state, s.postal_code, s.country]),
    );
  }

  return (
    <div>
      <header className="mb-6">
        <Link to="/segmentation" className="text-xs text-ink-500 hover:text-ink-900">
          ← All customers
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {data?.customer_name ?? (id ? `Customer ${id}` : "Customer")}
              </h1>
              {data && data.is_active === false && (
                <span className="rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
                  Departed
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-ink-500">ID: {id}</p>
            {primaryLocation && (
              <p className="mt-1 flex items-center gap-1 text-sm text-ink-700">
                <span aria-hidden>📍</span>
                <span>{primaryLocation}</span>
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <DateRangePicker
              value={range}
              presetId={presetId}
              onChange={(r, pid) => {
                setRange(r);
                setPresetId(pid);
              }}
            />
            <label className="flex items-center gap-2 text-xs text-ink-500">
              Product
              <select
                value={productGroupKey}
                onChange={(e) => setProductGroupKey(e.target.value as "catheters" | "all")}
                className="rounded-md border border-ink-200 px-2 py-1 text-xs text-ink-900"
              >
                <option value="catheters">Catheters</option>
                <option value="all">All products</option>
              </select>
            </label>
          </div>
        </div>
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">{(error as Error).message}</div>
      )}

      <section className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Quantity" value={fmtInt(total.qty)} loading={isLoading} />
        <Stat label="Revenue" value={fmtUSDCompact(total.revenue)} loading={isLoading} />
        <Stat label="Last order (week of)" value={fmtDate(total.lastOrder)} loading={isLoading} />
        <Stat
          label="Monitors installed"
          value={fmtInt(deviceUsage.data?.monitor_count)}
          loading={deviceUsage.isLoading}
        />
      </section>

      {id && (
        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ContactsEditor customerId={id} />
          <SalesRepsEditor customerId={id} />
        </div>
      )}

      <section className="card mb-6 p-5">
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-ink-900">Monthly sales — year over year</h2>
          {!!yoy.data?.cells.length && <DownloadButton onClick={exportYoy} label="Excel" />}
        </div>
        {yoy.isLoading ? (
          <div className="h-[320px] animate-pulse rounded bg-ink-100" />
        ) : yoy.error ? (
          <p className="text-sm text-danger">{(yoy.error as Error).message}</p>
        ) : yoy.data ? (
          <MonthlyYoyTable data={yoy.data} />
        ) : null}
      </section>

      <section className="card mb-6 p-5">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-medium text-ink-900">Catheters per monitor per month</h2>
          <span className="text-xs text-ink-500">
            {deviceUsage.data
              ? `${deviceUsage.data.monitor_count} monitors total`
              : ""}
          </span>
        </div>
        {deviceUsage.isLoading ? (
          <div className="h-[280px] animate-pulse rounded bg-ink-100" />
        ) : !deviceUsage.data?.monthly.length ? (
          <div className="py-10 text-center text-sm text-ink-500">
            {(deviceUsage.data?.monitor_count ?? 0) === 0
              ? "No monitors installed for this customer."
              : "No catheter orders in this window."}
          </div>
        ) : (
          <MonthlyUsageChart data={deviceUsage.data.monthly} />
        )}
      </section>

      <section className="card mb-6 p-5">
        <h2 className="mb-3 text-sm font-medium text-ink-900">Weekly quantity</h2>
        {isLoading ? (
          <div className="h-[220px] animate-pulse rounded bg-ink-100" />
        ) : (data?.weeks.length ?? 0) === 0 ? (
          <div className="py-10 text-center text-sm text-ink-500">No orders in this window.</div>
        ) : (
          <WeeklyBarChart data={data!.weeks.map((w) => ({ week: w.week, qty: w.qty }))} />
        )}
      </section>

      <section className="card mb-6 p-5">
        <h2 className="mb-3 text-sm font-medium text-ink-900">Weekly heatmap</h2>
        {isLoading ? (
          <div className="h-12 animate-pulse rounded bg-ink-100" />
        ) : (
          <WeeklyHeatmap data={data?.weeks ?? []} />
        )}
      </section>

      {(locations.data?.locations.length ?? 0) > 0 && (
        <section className="card mb-6 p-5">
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-medium text-ink-900">Ship-to locations</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-500">
                {locations.data!.locations.length} site
                {locations.data!.locations.length === 1 ? "" : "s"}
              </span>
              <DownloadButton onClick={exportLocations} label="Excel" />
            </div>
          </div>
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="py-2 text-left">Site</th>
                <th className="py-2 text-left">State</th>
                <th className="py-2 text-left">Postal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {locations.data!.locations.map((s) => (
                <tr key={s.ship_to_name}>
                  <td className="py-2">{s.ship_to_name}</td>
                  <td className="py-2 text-ink-700">{s.state ?? "—"}</td>
                  <td className="py-2 tabular-nums text-ink-700">{s.postal_code ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {id && (
        <div className="mb-6">
          <ProfileEditor customerId={id} />
        </div>
      )}

      <section className="card p-5">
        <div className="mb-3 flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-ink-900">Monthly totals</h2>
          {monthly.length > 0 && <DownloadButton onClick={exportMonthly} label="Excel" />}
        </div>
        {monthly.length === 0 ? (
          <p className="text-sm text-ink-500">—</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="py-2 text-left">Month</th>
                <th className="py-2 text-right">Qty</th>
                <th className="py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {monthly.map((m) => (
                <tr key={m.month}>
                  <td className="py-2">{m.month}</td>
                  <td className="py-2 text-right tabular-nums">{fmtInt(m.qty)}</td>
                  <td className="py-2 text-right tabular-nums text-ink-700">{fmtUSDCompact(m.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, loading }: { label: string; value: string; loading?: boolean }) {
  return (
    <div className="card p-5">
      <div className="stat-label">{label}</div>
      {loading ? (
        <div className="mt-2 h-7 w-20 animate-pulse rounded bg-ink-100" />
      ) : (
        <div className="mt-2 text-xl font-semibold tabular-nums">{value}</div>
      )}
    </div>
  );
}
